import React from 'react';
import { useEffect, useState} from 'react';
import { Typography, TextField, Stack, Box, Dialog, DialogTitle, IconButton, DialogContent, Chip, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Divider, TextareaAutosize, FormGroup, FormControlLabel, Checkbox, DialogContentText} from '@mui/material';
import { blue} from '@mui/material/colors';
import { Close} from '@mui/icons-material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

import FilerFine from '../dataObjects/FilerFine';
import { NoticeTypeEnums } from '../dataObjects/NoticeTypeEnums';
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import { WaiverStatusTypeEnums } from '../dataObjects/WaiverStatusTypeEnums';
import { WaiverRequestCategoryEnums } from '../dataObjects/waiverRequestCategoryEnums';

interface DocumentViewerProps {
    isDocumentViewerOpen: boolean;
    closeDocumentViewer: (reload: boolean) => void;
    currentFineInfo?: FilerFine | null;
    currentWaiverInfo?: FilerFine | null;
    currentNoticeUrl?: string;
    isEmailNotice?: boolean;
    isPaperNotice?: boolean;
    noticeTypeId?: number;
}

//<DialogContentText className="letter-preview-content" dangerouslySetInnerHTML={{ __html: letterContent }}></DialogContentText>
//For approved waiver <iframe src={'https://ocpf2.blob.core.windows.net/pdf/filers/16537/16537-waiverreq-1-031225.pdf'} width="99%" height={frameHeight} />

const DocumentViewer = (props: DocumentViewerProps): JSX.Element => {

    const [emailContent, setEmailContent] = useState('');
    const [draftNoticeUrl, setDraftNoticeUrl] = useState<string | null>(null);
    const frameHeight = '800px';
    const [directorDecisionTypeId, setDirectorDecisionTypeId] = useState(0);
    const [fineExpirationDate, setFineExpirationDate] = useState("");
    const [isWaiverRedacted, setWaiverRedacted] = useState(false);

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    useEffect(() => {
        //create 1st/2nd paper notice pdf?
        if (!props.currentNoticeUrl && props.isPaperNotice) {
            let id = props.currentFineInfo?.fineIssueId;
            let noticeTypeId = props.noticeTypeId;
            let draftPdfUrl = `/FineTracker/GetPdfNotice?fineIssueId=${id}&fineNoticeType=${noticeTypeId}`;
            setDraftNoticeUrl(draftPdfUrl);
        }

        //create 2nd email notice?
        if (!props.currentNoticeUrl && props.isEmailNotice) {
            getDraftEmailContent();
        }

    }, [props.isPaperNotice, props.isEmailNotice]);

    const displayWaiverRequest = (waiver: FilerFine) => {

        let waiverCategoryTypeId = waiver.waiverRequestCategory;

        return <Box sx={{ width: '100%', p: 2, border: '1px solid grey', bgcolor: '#fafafa' }}>
            <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={550} display={'flex'} justifyContent={'center'} >
                    Form CPF W-1: Request for Waiver of Penalty Pursuant to 970 CMR 2.14(6)
                </Typography>
                <Typography variant="subtitle2" display={'flex'} justifyContent={'center'} alignItems={"center"} >
                    Pursuant to 970 CMR 2.14(6), the undersigned hereby requests a waiver of the penalty assessed in
                    accordance with M.G.L. c. 55, §3 for the following good cause.
                </Typography>
                <FormGroup>
                    <FormControlLabel control={<Checkbox checked={waiverCategoryTypeId === WaiverRequestCategoryEnums.Incapactiation} />} label={'Incapacitating illness or hospitalization of candidate, treasurer, or other person assessed a penalty, or immediate family member of such person.'} />
                    <FormControlLabel control={<Checkbox checked={waiverCategoryTypeId === WaiverRequestCategoryEnums.Death} />} label={'Death of candidate, treasurer, or other person assessed a penalty, or immediate family member of such person.'} />
                    <FormControlLabel control={<Checkbox checked={waiverCategoryTypeId === WaiverRequestCategoryEnums.Accident} />} label={'Serious accident involving candidate, treasurer, or other person assessed a penalty.'} />
                    <FormControlLabel control={<Checkbox checked={waiverCategoryTypeId === WaiverRequestCategoryEnums.Weather} />} label={'Unforeseen severe weather conditions.'} />
                    <FormControlLabel control={<Checkbox checked={waiverCategoryTypeId === WaiverRequestCategoryEnums.Other} />} label={'Other (specify)'} />
                </FormGroup>
                <Typography variant="subtitle2" sx={{ color: blue[800] }} component={'i'}>
                    {waiver.waiverRequestNote}
                </Typography>
                <Divider sx={{ marginTop: "20px", marginBottom: "20px" }} />
                <Typography variant="subtitle2">
                    Submitted on: {waiver.waiverSubmissionDate}
                </Typography>
            </Stack>
        </Box>
    }

    const getDraftEmailContent = () => {
        let fineIssueId = props.currentFineInfo?.fineIssueId;
        fetch(`/FineTracker/GetHtmlNotice?fineIssueId=${fineIssueId}&fineNoticeType=2`)
            .then(result => result.text())
            .then(result => {
                setEmailContent(result);
            }
         )
    }

    const updateDirectorDecision = (event: SelectChangeEvent) => {
        let type = event.target.value;
        setDirectorDecisionTypeId(Number(type));
    };

    const updateFineExpirationDate = (date: Dayjs | null) => {
        let expirationDate = date?.toDate().toLocaleDateString();
        setFineExpirationDate(expirationDate || '');
    };

    const updateWaiverRedaction = (event: React.ChangeEvent<HTMLInputElement>) => {
        setWaiverRedacted(event.target.checked);
    }

    const saveDecision = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
       
        let params = [
            `fineIssueId=${props.currentWaiverInfo?.fineIssueId}`,
            `auditPeriodId=${props.currentWaiverInfo?.auditPeriodId}`,
            `cpfId=${props.currentWaiverInfo?.cpfId}`,
            `directorWaiverDecisionTypeId=${formData.get('DirectorWaiverDecisionTypeId')}`,
            `directorWaiverDecisionNote=${encodeURIComponent(formData.get('DirectorWaiverDecisionNote') as string || '')}`,
            `reducedAmount=${formData.get('ReducedAmount')}`,
            `isWaiverRequestRedacted=${isWaiverRedacted}`,
            `fineReductionExpirationDate=${fineExpirationDate}`,
        ].join('&');


        fetch(`/FineTracker/SaveWaiverDetermination?${params}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveWaiverDetermination',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    props.closeDocumentViewer(true);
                }
            }
        )
    }

    const emailFineNotice = () => {

        let fineIssueId = props.currentFineInfo?.fineIssueId;
        let url = `/FineTracker/EmailNotice?fineIssueId=${fineIssueId}&fineNoticeType=${NoticeTypeEnums.Second}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: EmailNotice',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'success',
                        title: 'Email sent',
                        errorMessage: 'The fine notice email was successfully sent',
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);

                    //refresh payload
                    props.closeDocumentViewer(true);;
                }
            })
    }

    return (
        <>
            <Dialog
                open={props.isDocumentViewerOpen}
                scroll={'paper'}
                fullWidth={true}
                maxWidth={'md'}
                keepMounted
                onClose={props.closeDocumentViewer}
                sx={{
                    "& .MuiDialog-container": {
                        justifyContent: "flex-end",
                        alignItems: "flex-end"
                    }
                }}
                PaperProps={{
                    sx: {
                        m: 0,
                        top: 0,
                        right: 0,
                        bottom: 0,
                        minHeight: '100vh'
                    }
                }}
            >
                <DialogTitle>
                    {
                        props.currentFineInfo &&
                        <Stack direction="row" spacing={2} alignItems="center">
                            {
                                props.currentNoticeUrl && props.isEmailNotice &&
                                <Chip label="Email Sent" color="success" />
                            }
                            {
                                !props.currentNoticeUrl && props.isEmailNotice &&
                                <Chip label="Draft Email" color="warning" />
                            }
                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>
                                {props.currentFineInfo.cpfId} {props.currentFineInfo.fullName}
                            </Typography>
                            {
                                props.isPaperNotice &&
                                <PictureAsPdfIcon />
                            }
                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>
                                {props.isEmailNotice ? "Email Notice" : "Mail Notice"}
                            </Typography>
                            {
                                props.noticeTypeId === NoticeTypeEnums.Initial && props.currentFineInfo.initialNoticePaperDate &&
                                <Typography variant="subtitle1" component={'i'}>
                                    1st Paper Notice sent on {props.currentFineInfo.initialNoticePaperDate}
                                </Typography>
                            }
                            {
                                props.noticeTypeId === NoticeTypeEnums.Second && props.currentFineInfo.secondNoticePaperDate &&
                                <Typography variant="subtitle1" component={'i'}>
                                    2nd Paper Notice sent on {props.currentFineInfo.secondNoticePaperDate}
                                </Typography>
                            }
                        </Stack>
                    }
                    {
                        props.currentWaiverInfo &&
                        <Stack direction="row" spacing={2} alignItems="center">
                            {
                                props.currentWaiverInfo.waiverStatus === WaiverStatusTypeEnums.Processed ?
                                    <Chip label="Processed" color="primary" />
                                    :
                                    <Chip label="Pending" color="warning" />
                            }
                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>
                                {props.currentWaiverInfo.cpfId} {props.currentWaiverInfo.fullName}:
                            </Typography>
                            <Typography variant="subtitle1" sx={{ color: blue[800] }} component={'i'}>
                                Waiver for {props.currentWaiverInfo.modifiedFineAmount} fine
                            </Typography>
                        </Stack>
                    }
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={()=>props.closeDocumentViewer(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <Close />
                </IconButton>
                <DialogContent dividers>
                    {/*
                     1st e-notice: audit letter, using letter url
                     2nd e-notice: draft: generate email content; sent: use url
                     1st paper: draftUrl (generate PDF)
                     2nd paer: draftUrl (generate PDF)
                    */}
                    {
                        props.currentNoticeUrl && props.isEmailNotice &&
                        <iframe src={props.currentNoticeUrl} width="99%" height={frameHeight} />
                    }
                    {
                        !props.currentNoticeUrl && props.isEmailNotice &&
                        <DialogContentText dangerouslySetInnerHTML={{ __html: emailContent }}></DialogContentText>
                    }
                    {
                        !props.currentNoticeUrl && draftNoticeUrl && props.isPaperNotice &&
                        <iframe src={draftNoticeUrl} width="99%" height={frameHeight} />
                    }
                    {
                        props.currentWaiverInfo &&
                        <>
                            {
                                props.currentWaiverInfo.waiverStatus === WaiverStatusTypeEnums.Processed &&
                                <Typography variant="subtitle1" marginBottom={2}>
                                    Director Decision: {props.currentWaiverInfo.waiverDecisionTypeDescription} ({props.currentWaiverInfo.waiverDecisionDate})
                                </Typography>
                            }
                            {
                                props.currentWaiverInfo.waiverStatus === WaiverStatusTypeEnums.Processed  &&
                                <iframe src={props.currentWaiverInfo.waiverUrl} width="99%" height={frameHeight} />
                            }
                            {
                                props.currentWaiverInfo.waiverStatus === WaiverStatusTypeEnums.Pending &&
                                displayWaiverRequest(props.currentWaiverInfo)
                            }
                            <Divider sx={{ marginTop: "20px", marginBottom: "20px" }} />
                                {/* waiver approval form */}
                            {
                                props.currentWaiverInfo.waiverStatus === WaiverStatusTypeEnums.Pending && 
                                <form onSubmit={saveDecision} id="decisionForm" noValidate autoComplete="off" >
                                        <Stack spacing={2}>
                                            {
                                                <Stack direction="row" spacing={1} >
                                                    <FormControl>
                                                        <InputLabel>Director Decision</InputLabel>
                                                        <Select
                                                            defaultValue={props.currentWaiverInfo?.waiverDecisionType.toString() || ""}
                                                            label="Director Decision"
                                                            onChange={updateDirectorDecision}
                                                            sx={{ width: 200 }}
                                                            name="DirectorWaiverDecisionTypeId"
                                                        >
                                                            <MenuItem value={0}>None</MenuItem>
                                                            <MenuItem value={2}>Waived</MenuItem>
                                                            <MenuItem value={3}>Reduced</MenuItem>
                                                            <MenuItem value={10}>Denied</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                    <FormControlLabel
                                                        control={<Checkbox onChange={updateWaiverRedaction} defaultChecked={props.currentWaiverInfo.isWaiverRequestRedacted ? true : false} />}
                                                        label="Redact reason?" />
                                                </Stack>
                                            }
                                        
                                            {
                                                directorDecisionTypeId === 3 &&
                                                <Stack direction="row" spacing={1} >
                                                        <TextField
                                                            name="ReducedAmount"
                                                            label="Type Reduced Amount"
                                                            variant="outlined"
                                                            sx={{ width: 200 }}
                                                        />
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                onChange={updateFineExpirationDate}
                                                                sx={{ width: 300 }}
                                                                defaultValue={props.currentWaiverInfo.fineReductionExpirationDate ? dayjs(props.currentWaiverInfo.fineReductionExpirationDate) : null}
                                                                label="Paid By Date" />
                                                        </LocalizationProvider>
                                                </Stack>
                                        }
                                        <TextField
                                            aria-label="director notes"
                                            minRows={2}
                                            multiline
                                            name="DirectorWaiverDecisionNote"
                                            placeholder="Type Director Note..."
                                            defaultValue={props.currentWaiverInfo.waiverDecisionNote}
                                            style={{ width: 400 }}
                                        />
                                    </Stack>
                                </form>
                            }
                        </>
                    }
                </DialogContent>
                <DialogActions>
                    {
                        props.isEmailNotice && props.noticeTypeId === NoticeTypeEnums.Second && !props.currentNoticeUrl && <Button variant="contained" color="success" onClick={emailFineNotice}>Send</Button>
                    }
                    {
                        props.isEmailNotice && props.noticeTypeId === NoticeTypeEnums.Second && props.currentNoticeUrl && <Button variant="contained" color="success" onClick={emailFineNotice}>Resend</Button>
                    }
                    {
                        props.isEmailNotice && props.noticeTypeId === NoticeTypeEnums.Second && <Button variant="contained" color="primary">Email to me</Button>
                    }
                    {
                        props.currentWaiverInfo &&
                        <Button type="submit" form="decisionForm" variant="contained" color="success">Save Decision</Button>
                    }
                    <Button onClick={() => props.closeDocumentViewer(false)} variant="contained" color="warning">Cancel</Button>
                </DialogActions>
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
        
    );
};

export default DocumentViewer;